import { ComponentProps, memo, useMemo } from 'react'
import { useFormState } from 'react-final-form'
import { FormItem } from '@cargomart/ui'
import get from 'lodash/get'

interface IProps extends Omit<ComponentProps<typeof FormItem>, 'error' | 'name'> {
  name: string
  isDontWaitTouched?: boolean
  validateError?: (error: string) => boolean | string
}

const isNotRequiredError = (error: string): boolean => error !== 'Обязательное поле'

export const FieldFormItem = memo(function FieldFormItem({
  children,
  name,
  isDontWaitTouched,
  validateError = isNotRequiredError,
  ...rest
}: IProps) {
  const { dirtyFieldsSinceLastSubmit, errors, submitErrors, touched, submitting } = useFormState({
    subscription: {
      dirtyFieldsSinceLastSubmit: true,
      errors: true,
      submitErrors: true,
      touched: true,
      submitting: true
    }
  })

  const errorsElements = useMemo(() => {
    const preparedErrors = name
      .split(';')
      .map((i) => {
        const error = get(errors, i)
        const submitError = get(submitErrors, i)
        if (isDontWaitTouched && error) {
          return error
        }

        if (get(touched, i) && error) {
          return error
        }

        if (submitting) {
          return
        }

        if (!get(dirtyFieldsSinceLastSubmit, i)) {
          return submitError
        }

        return undefined
      })
      .filter(validateError)
      .filter(Boolean)
    if (preparedErrors.length === 0) {
      return ''
    }

    if (preparedErrors.length === 1) {
      return typeof preparedErrors[0] === 'string'
        ? preparedErrors[0]
        : preparedErrors[0][Object.keys(preparedErrors[0])[0]]
    }

    return (
      <ol className="list-disc pl-5">
        {preparedErrors.map((error, i) => (
          <li key={i}>{error}</li>
        ))}
      </ol>
    )
  }, [name, validateError, errors, submitErrors, isDontWaitTouched, touched, submitting, dirtyFieldsSinceLastSubmit])

  return (
    <FormItem error={errorsElements} name={name} {...rest}>
      {children}
    </FormItem>
  )
})
